import { DEFAULT_LOCALE } from "../money";
import { hasWord, word } from "../wording";
import type { MaintenanceNotice } from "./notice";

/** The words for the full-screen "Back soon" scene. */
export type MaintenanceScreenCopy = {
  readonly title: string;
  readonly text: string;
  /** The team's one extra line for this window, when there is one. */
  readonly note?: string;
  readonly small: string;
};

type CopyOptions = {
  readonly now?: Date;
  readonly locale?: string;
};

function formatUntil(end: Date, locale: string): string {
  // No time zone is named, so the browser's own is used: the end time reads in the viewer's local time.
  return new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" }).format(end);
}

function keepsWorkingLine(key: string | undefined, locale: string): string {
  const code = `maintenance.keepsWorking.${key}`;
  return key !== undefined && hasWord(code, locale) ? word(code, {}, locale) : "";
}

/**
 * Chooses the scene's words: the end time in the viewer's zone, "Nearly there" once that time has
 * passed, and a line without a time when the edge has none to give.
 */
export function maintenanceScreenCopy(notice: MaintenanceNotice, { now = new Date(), locale = DEFAULT_LOCALE }: CopyOptions = {}): MaintenanceScreenCopy {
  const parsed = notice.until === undefined ? undefined : new Date(notice.until);
  const end = parsed !== undefined && !Number.isNaN(parsed.getTime()) ? parsed : undefined;
  const keepsWorking = keepsWorkingLine(notice.keepsWorkingKey, locale);
  const small = word("scene.maintenance.refreshes", {}, locale);
  const withNote = notice.note === undefined ? {} : { note: notice.note };

  // The edge says when the work has run over; a time that has passed by the viewer's clock says the same.
  if (notice.overdue === true || (end !== undefined && end.getTime() <= now.getTime())) {
    return { title: word("scene.maintenance.overdue.title", {}, locale), text: word("scene.maintenance.overdue.text", {}, locale), ...withNote, small };
  }
  const text = end !== undefined
    ? word("scene.maintenance.default.text", { until: formatUntil(end, locale), keepsWorking }, locale)
    : word("scene.maintenance.noEnd.text", { keepsWorking }, locale);
  return { title: word("scene.maintenance.default.title", {}, locale), text: text.replace(/\s{2,}/g, " ").trim(), ...withNote, small };
}
